import * as Colyseus from "colyseus.js";
import { useCallback, useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import * as uf from "../../../../common/unfathomable";

const kPlayerIdKey = "unfathomable-player-id";
const kNameKey = "unfathomable-name";

export interface RoomListing {
  roomId: string;
  displayName: string;
  clients: number;
}

/** Stable per-browser id; `?pid=x` in the URL derives a separate identity (handy for playing several seats on one machine). */
export function playerId(): string {
  let id = localStorage.getItem(kPlayerIdKey);
  if (!id) { id = uuidv4(); localStorage.setItem(kPlayerIdKey, id); }
  const pid = new URLSearchParams(window.location.search).get("pid");
  return pid ? `${id}:${pid}` : id;
}

function roomKey(): string {
  const pid = new URLSearchParams(window.location.search).get("pid");
  return pid ? `unfathomable-room:${pid}` : "unfathomable-room";
}

function nameKey(): string {
  const pid = new URLSearchParams(window.location.search).get("pid");
  return pid ? `${kNameKey}:${pid}` : kNameKey;
}

export function savedName(): string {
  return localStorage.getItem(nameKey()) ?? "";
}

/** HTTP base of the game server (for /auth): same origin under /uf in production. */
export function gameHttpBase(): string {
  const env = process.env.REACT_APP_UNFATHOMABLE_GAME_BACKEND_URL;
  if (env) return env.replace(/^ws/, "http");
  return `${window.location.origin}/uf`;
}

let client: Colyseus.Client | null = null;
function getClient(): Colyseus.Client {
  // Production: same origin under /uf (Caddy proxies it to port 5003); dev: REACT_APP_UNFATHOMABLE_GAME_BACKEND_URL.
  const fallback = `${window.location.protocol === "https:" ? "wss" : "ws"}://${window.location.host}/uf`;
  if (!client) client = new Colyseus.Client(process.env.REACT_APP_UNFATHOMABLE_GAME_BACKEND_URL ?? fallback);
  return client;
}

export interface UnfathomableConnection {
  rooms: RoomListing[];
  refreshRooms: () => Promise<RoomListing[]>;
  joined: boolean;
  msg: uf.RoomMessage | null;
  error: string | null;
  clearError: () => void;
  join: (roomId: string, name: string, displayName?: string) => Promise<void>;
  leave: () => void;
  send: (type: string, payload?: unknown) => void;
  answer: (decisionId: number, choice: string[]) => void;
  requestBackup: () => Promise<uf.GameBackup | null>;
  requestTranscript: () => Promise<string | null>;
}

export function useUnfathomableRoom(): UnfathomableConnection {
  const [rooms, setRooms] = useState<RoomListing[]>([]);
  const [msg, setMsg] = useState<uf.RoomMessage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [joined, setJoined] = useState(false);
  const roomRef = useRef<Colyseus.Room | null>(null);
  const backupWaiter = useRef<((b: uf.GameBackup | null) => void) | null>(null);
  const transcriptWaiter = useRef<((t: string | null) => void) | null>(null);

  const refreshRooms = useCallback(() => getClient().getAvailableRooms(uf.kRoomName).then(list => {
    const rs = list.map(r => ({ roomId: r.roomId, displayName: r.metadata?.displayName ?? r.roomId, clients: r.clients }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
    setRooms(rs);
    return rs;
  }).catch(e => { setError(`无法连接服务器：${e?.message ?? e}`); return [] as RoomListing[]; }), []);

  const joining = useRef(false);
  const reconnectLater = useRef<((displayName: string, attempt: number) => void) | null>(null);
  const join = useCallback(async (roomId: string, name: string, displayName?: string) => {
    if (joining.current) return;
    joining.current = true;
    localStorage.setItem(nameKey(), name);
    try {
      const options: uf.JoinOptions = { playerId: playerId(), name };
      const room = await getClient().joinById(roomId, options);
      roomRef.current = room;
      room.onMessage(uf.Api.kView, (m: uf.RoomMessage) => setMsg(m));
      room.onMessage(uf.Api.kError, (m: { error: string }) => setError(m.error));
      room.onMessage(uf.Api.kBackup, (m: { backup: uf.GameBackup | null }) => {
        backupWaiter.current?.(m.backup);
        backupWaiter.current = null;
      });
      room.onMessage("transcript", (m: { text: string | null }) => {
        transcriptWaiter.current?.(m.text);
        transcriptWaiter.current = null;
      });
      room.onLeave((code) => {
        if (roomRef.current !== room) return; // a stale connection (e.g. after a hot reload)
        setJoined(false); roomRef.current = null; setMsg(null);
        // Unexpected disconnect (server restart, network): rejoin the same room by name.
        const last = sessionStorage.getItem(roomKey());
        if (code !== 1000 && last) reconnectLater.current?.(last, 0);
      });
      if (displayName) sessionStorage.setItem(roomKey(), displayName);
      setJoined(true);
    } catch (e: any) {
      setError(`加入房间失败：${e?.message ?? e}`);
    } finally {
      joining.current = false;
    }
  }, []);

  const leave = useCallback(() => {
    sessionStorage.removeItem(roomKey());
    roomRef.current?.leave();
    roomRef.current = null;
    setJoined(false);
    setMsg(null);
    refreshRooms();
  }, [refreshRooms]);

  reconnectLater.current = (displayName: string, attempt: number) => {
    if (attempt > 20) { setError("与服务器的连接已断开"); return; }
    setTimeout(() => {
      if (roomRef.current) return;
      refreshRooms().then(rs => {
        const r = rs.find(x => x.displayName === displayName);
        if (r) join(r.roomId, savedName(), r.displayName);
        else reconnectLater.current?.(displayName, attempt + 1);
      });
    }, Math.min(1000 * 2 ** Math.min(attempt, 4), 10000));
  };

  // Initial room list, and automatic rejoin after a page reload (the server keeps the seat).
  useEffect(() => {
    refreshRooms().then(rs => {
      const last = sessionStorage.getItem(roomKey());
      const r = last ? rs.find(x => x.displayName === last) : undefined;
      if (r && !roomRef.current) join(r.roomId, savedName(), r.displayName);
    });
  }, [refreshRooms, join]);

  const send = useCallback((type: string, payload?: unknown) => {
    roomRef.current?.send(type, payload ?? {});
  }, []);

  const answer = useCallback((decisionId: number, choice: string[]) => {
    const req: uf.Api.AnswerRequest = { decisionId, choice };
    roomRef.current?.send(uf.Api.kAnswer, req);
  }, []);

  const requestBackup = useCallback(() => new Promise<uf.GameBackup | null>(resolve => {
    backupWaiter.current = resolve;
    roomRef.current?.send(uf.Api.kRequestBackup, {});
  }), []);

  const requestTranscript = useCallback(() => new Promise<string | null>(resolve => {
    transcriptWaiter.current = resolve;
    roomRef.current?.send("requestTranscript", {});
  }), []);

  useEffect(() => () => { roomRef.current?.leave(); }, []);

  return { rooms, refreshRooms, joined, msg, error, clearError: () => setError(null), join, leave, send, answer, requestBackup, requestTranscript };
}
